import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { actions } from '../../actions';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from '../../components/ui/use-toast';
import { services } from '../../services';
import { Asset } from '../../types/asset';
import {
  CategoryRecord,
  getCategoryDisplayName,
  getCategoryId,
} from '../../utils/categoryUtils';

interface EditAssetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: Asset | null;
  onSave?: (asset: Asset) => void;
}

export function EditAssetModal({ open, onOpenChange, asset, onSave }: EditAssetModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<Asset>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categoriesList, setCategoriesList] = useState<CategoryRecord[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  const loadCategories = async () => {
    const response = await actions.getAssetCategories();
    setCategoriesList(response.data || []);
  };

  useEffect(() => {
    if (open) {
      loadCategories().catch(() => {
        toast({ description: 'Failed to load categories', variant: 'destructive' });
      });
    }
  }, [open]);

  useEffect(() => {
    if (asset && open) {
      setFormData({
        ...asset,
        name: asset.name || asset.assetName,
        totalTokens: asset.totalTokens || asset.maxSupply || 0,
        yieldRate: asset.yieldRate || asset.annualYield || 0,
        categoryId: asset.categoryId,
        categoryName: asset.categoryName,
      });
    }
  }, [asset, open]);

  const handleAddCategory = async () => {
    const trimmedName = newCategoryName.trim();
    if (!trimmedName) {
      toast({ description: 'Enter a category name', variant: 'destructive' });
      return;
    }

    setIsAddingCategory(true);
    try {
      const created = await actions.createCategory(trimmedName);
      setNewCategoryName('');
      await loadCategories();
      setFormData((prev) => ({
        ...prev,
        categoryId: getCategoryId(created),
        categoryName: getCategoryDisplayName(created),
      }));
      toast({ description: `Category "${trimmedName}" added`, variant: 'default' });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to add category. It may already exist.';
      toast({ description: message, variant: 'destructive' });
    } finally {
      setIsAddingCategory(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.location || !formData.tokenSymbol) {
      toast({ description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    if (!asset || (!asset.id && !asset._id)) {
      toast({ description: 'Asset ID is required', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);

    try {
      const updateFormData = new FormData();
      const changedFields: Record<string, string | number> = {};

      if (formData.name !== asset.name && formData.name !== asset.assetName) {
        changedFields.assetName = formData.name;
      }
      if (formData.location !== asset.location) {
        changedFields.location = formData.location;
      }
      if (formData.description !== asset.description) {
        changedFields.description = formData.description || '';
      }
      if (formData.totalTokens !== (asset.totalTokens || asset.maxSupply)) {
        changedFields.maxSupply = formData.totalTokens || 0;
      }
      if (formData.yieldRate !== (asset.yieldRate || asset.annualYield)) {
        changedFields.annualYield = formData.yieldRate || 0;
      }
      if (formData.valuation !== asset.valuation) {
        changedFields.valuation = formData.valuation || 0;
      }
      if (formData.categoryId && formData.categoryId !== asset.categoryId) {
        changedFields.category = formData.categoryId;
      }
      if (formData.isFeatured !== asset.isFeatured) {
        changedFields.isFeatured = formData.isFeatured ? 'true' : 'false';
      }

      Object.keys(changedFields).forEach((key) => {
        updateFormData.append(key, changedFields[key].toString());
      });

      if (Object.keys(changedFields).length === 0) {
        toast({ description: 'No changes to save', variant: 'default' });
        setIsSubmitting(false);
        return;
      }

      const response = await services.put(`asset/update/${asset.id || asset._id}`, updateFormData);

      if (response?.status === 200) {
        toast({
          description: 'Asset updated successfully',
          variant: 'default',
        });
        queryClient.invalidateQueries({ queryKey: ['assets'] });
        onSave?.(formData as Asset);
        onOpenChange(false);
      } else {
        throw new Error('Failed to update asset');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      toast({
        description: axiosError?.response?.data?.message || 'Failed to update asset',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!asset) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[550px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            Edit Asset
            <code className='text-sm font-mono text-primary bg-primary/10 px-2 py-0.5 rounded'>
              ${formData.tokenSymbol || asset.tokenSymbol}
            </code>
          </DialogTitle>
          <DialogDescription>Update asset details and tokenization parameters.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-4 py-4'>
          <div className='space-y-2'>
            <Label htmlFor='name'>
              Asset Name <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='name'
              placeholder='e.g., Downtown Office Complex'
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='category'>Category</Label>
              <Select
                value={formData.categoryId || undefined}
                onValueChange={(value) => {
                  const selected = categoriesList.find(
                    (category) => getCategoryId(category) === value
                  );
                  setFormData({
                    ...formData,
                    categoryId: value,
                    categoryName: getCategoryDisplayName(selected),
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select category' />
                </SelectTrigger>
                <SelectContent>
                  {categoriesList.map((category) => {
                    const categoryId = getCategoryId(category);
                    return (
                      <SelectItem key={categoryId} value={categoryId}>
                        {getCategoryDisplayName(category)}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <div className='flex gap-2'>
                <Input
                  placeholder='Add new category'
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAddCategory();
                    }
                  }}
                />
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => void handleAddCategory()}
                  disabled={isAddingCategory || !newCategoryName.trim()}
                >
                  {isAddingCategory ? <Loader2 className='w-4 h-4 animate-spin' /> : 'Add'}
                </Button>
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='location'>
                Location <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='location'
                placeholder='City, State, Country'
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              />
            </div>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='description'>Description</Label>
            <Textarea
              id='description'
              placeholder='Enter asset description'
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='valuation'>Valuation ($)</Label>
              <Input
                id='valuation'
                type='number'
                placeholder='10000000'
                value={formData.valuation || ''}
                onChange={(e) => setFormData({ ...formData, valuation: Number(e.target.value) })}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='yieldRate'>Yield Rate (%)</Label>
              <Input
                id='yieldRate'
                type='number'
                step='0.1'
                placeholder='8.5'
                value={formData.yieldRate || ''}
                onChange={(e) => setFormData({ ...formData, yieldRate: Number(e.target.value) })}
              />
            </div>
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='totalTokens'>Total Token Supply</Label>
              <Input
                id='totalTokens'
                type='number'
                placeholder='100000'
                value={formData.totalTokens || ''}
                onChange={(e) => setFormData({ ...formData, totalTokens: Number(e.target.value) })}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='tokensIssued'>Tokens Issued</Label>
              <Input
                id='tokensIssued'
                type='number'
                placeholder='50000'
                value={formData.tokensIssued || ''}
                onChange={(e) => setFormData({ ...formData, tokensIssued: Number(e.target.value) })}
              />
            </div>
          </div>

          <div className='flex items-start gap-3 rounded-lg border border-border/50 p-4'>
            <Checkbox
              id='editIsFeatured'
              checked={formData.isFeatured === true}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, isFeatured: checked === true })
              }
            />
            <div className='space-y-1'>
              <Label htmlFor='editIsFeatured' className='cursor-pointer'>
                Mark as featured investment
              </Label>
              <p className='text-xs text-muted-foreground'>
                Only one asset can be featured at a time. This replaces any existing featured
                asset.
              </p>
            </div>
          </div>

          <DialogFooter className='pt-4'>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
